import React, { useEffect, useMemo, useState } from 'react';
import { Content } from '@backstage/core-components';
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  Typography,
} from '@material-ui/core';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import SupportRequestHeader from '../SupportRequestHeader';
import ConfirmationNumberIcon from '@material-ui/icons/ConfirmationNumber';
import ReceiptIcon from '@material-ui/icons/Receipt';
import TimerIcon from '@material-ui/icons/Timer';
import PersonIcon from '@material-ui/icons/Person';
import AssignAdminModal from '../AssignAdminModal';
import EditStatusModal from '../EditStatusModal';
import UpdatedDateIcon from '../../icons/UpdatedDateIcon';
import CreateDateIcon from '../../icons/CreateDateIcon';
import { RequestStatus, SummaryItemConfig, IntakeRequest } from '../types';
import { useUserDateFormatter } from '../hooks';
import AttachmentsList from '../AttachmentsList';
import SummaryRequestView from '../SummaryRequestView/SummaryRequestView';
import StatusRequest from '../StatusRequest/StatusRequest';
import {
  SectionDivider,
  FieldLabelText,
  SectionTitle,
  ContentWrapper,
} from '../UserRequestsStyles';
import IntakeRequestTypeFields from './IntakeRequestTypeFields';
import { DetailsField } from '../DetailsField';
import ActivityPanel from '../ActivityPanel/ActivityPanel';
import { ActivityFormData } from '../ActivityPanel/types';
import { useUserRequestsPermissions } from '../hooks';
import {
  useIntakeRequestAttachments,
  useIntakeRequestDetails,
  useIntakeRequestLogs,
  useSendIntakeMessage,
  useUpdateIntakeRequestStatus,
} from '../hooks/useIntakeRequests';
import { FlexBox } from '../../global/FlexBox';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../../shared/api/getApiErrorMessage';
import { REQUEST_TYPE } from '../constants';

const getSummaryItemsConfig = (
  request: IntakeRequest,
  formatDate: (dateIso?: string | null) => string,
): SummaryItemConfig[] => [
  {
    icon: <ConfirmationNumberIcon />,
    label: 'Ticket Number',
    value: request.requestId,
  },
  {
    icon: <ReceiptIcon />,
    label: 'Ticket Type',
    value: request.ticketType,
  },
  {
    icon: <TimerIcon />,
    label: 'Status',
    value: <StatusRequest status={request.status} />,
  },
  {
    icon: <PersonIcon />,
    label: 'Assigned Admin',
    value: request.assignedAdmin ?? '-',
  },
  {
    icon: <CreateDateIcon />,
    label: 'Create Date',
    value: formatDate(request.createDate),
  },
  {
    icon: <UpdatedDateIcon />,
    label: 'Updated On',
    value: formatDate(request.updatedAt),
  },
];

const IntakeRequestDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const requestType =
    new URLSearchParams(location.search).get('type') || undefined;

  const { formatDateTime: formatDate, formatDate: formatDateOnly } =
    useUserDateFormatter();

  const {
    data: request,
    isLoading,
    isError,
    error,
  } = useIntakeRequestDetails({
    id,
    type: requestType,
  });
  const {
    mutateAsync: updateIntakeRequestStatus,
    isPending: isUpdatingStatus,
  } = useUpdateIntakeRequestStatus();
  const { mutateAsync: sendIntakeMessage, isPending: isSendingMessage } =
    useSendIntakeMessage();
  const {
    data: activityLogs,
    isLoading: isLogsLoading,
    isFetching: isLogsFetching,
  } = useIntakeRequestLogs({
    intakeId: id,
  });
  const { data: attachments } = useIntakeRequestAttachments({
    intakeId: id,
  });

  const [isReassignOpen, setIsReassignOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const { permissions } = useUserRequestsPermissions();
  const canWrite = !!permissions.intake.write;
  const [assignedAdminOverride, setAssignedAdminOverride] = useState<
    string | null
  >(null);

  const openReassign = () => setIsReassignOpen(true);
  const closeReassign = () => setIsReassignOpen(false);
  const openEdit = () => setIsEditOpen(true);
  const closeEdit = () => setIsEditOpen(false);
  const handleAssign = (newEmail: string) => {
    setAssignedAdminOverride(newEmail);
    closeReassign();
  };

  const handleSaveStatus = async (next: RequestStatus, reason?: string) => {
    if (!id || !requestType) return;
    if (isUpdatingStatus) return;

    try {
      await updateIntakeRequestStatus({
        id,
        type: requestType,
        status: next,
        reason,
      });
      closeEdit();
    } catch (statusError) {
      enqueueSnackbar(
        getApiErrorMessage(statusError, 'Failed to update request status'),
        {
          variant: 'error',
        },
      );
    }
  };

  const handleSendMessage = async (data: ActivityFormData) => {
    if (!id) return;

    try {
      await sendIntakeMessage({
        id,
        email: data.email,
        ccRecipients: data.ccRecipients,
        message: data.message,
      });
    } catch (messageError) {
      enqueueSnackbar(
        getApiErrorMessage(messageError, 'Failed to send message'),
        {
          variant: 'error',
        },
      );
    }
  };

  const displayRequest = useMemo<IntakeRequest | undefined>(() => {
    if (!request) return undefined;
    if (!assignedAdminOverride) return request;
    return { ...request, assignedAdmin: assignedAdminOverride };
  }, [assignedAdminOverride, request]);

  const isPageLoading = isLoading && !displayRequest;
  const activities = useMemo(
    () => activityLogs?.elements || [],
    [activityLogs],
  );
  const requestAttachments = useMemo(() => attachments || [], [attachments]);
  const isActivityLoading = isLogsLoading || isLogsFetching || isSendingMessage;

  useEffect(() => {
    if (!isError) return;

    enqueueSnackbar(
      getApiErrorMessage(error, 'Failed to load intake request'),
      {
        variant: 'error',
      },
    );
  }, [enqueueSnackbar, error, isError]);

  if (isPageLoading) {
    return (
      <Content>
        <FlexBox
          sx={{
            height: '100vh',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </FlexBox>
      </Content>
    );
  }

  if (!displayRequest) {
    return (
      <Content>
        <Typography variant="h6">Intake Request not found</Typography>
      </Content>
    );
  }

  const hasTypeSpecificDetails = Object.entries(
    displayRequest.requestTypeDetails,
  ).some(([key, value]) => {
    if (key === 'type') return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'boolean') return true;
    return Boolean(value);
  });

  return (
    <>
      <ContentWrapper>
        <Content>
          <SupportRequestHeader
            onBack={() => navigate('/user-requests/intake')}
            onReassign={openReassign}
            onEditStatus={openEdit}
            canWrite={canWrite}
          />
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <SectionTitle variant="h2">Intake Form Information</SectionTitle>
              <SummaryRequestView
                items={getSummaryItemsConfig(displayRequest, formatDate)}
              />

              <SectionDivider />

              <SectionTitle variant="h4">Ticket Details</SectionTitle>
              <Box style={{ marginTop: 16 }} />
              <Grid container>
                <Grid item xs={12} md={3}>
                  <DetailsField label="Name" value={displayRequest.name} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DetailsField
                    label="Request type"
                    value={displayRequest.requestType}
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  <DetailsField
                    label="Email Address"
                    value={
                      <Chip
                        color="primary"
                        label={displayRequest.email ?? '-'}
                      />
                    }
                  />
                </Grid>
              </Grid>

              {hasTypeSpecificDetails && (
                <>
                  <SectionDivider />
                  <IntakeRequestTypeFields
                    requestTypeDetails={displayRequest.requestTypeDetails}
                  />
                </>
              )}

              <SectionDivider />

              <FieldLabelText>Attachments</FieldLabelText>
              <AttachmentsList
                items={requestAttachments.map(att => ({
                  ...att,
                  uploadedAt: formatDateOnly(att.uploadedAt),
                }))}
              />
            </Grid>

            <Grid item xs={12} md={4} style={{ paddingBottom: 0 }}>
              <ActivityPanel
                readOnly={!canWrite}
                defaultEmail={displayRequest.email}
                activities={activities}
                isLoading={isActivityLoading}
                onSendMessage={handleSendMessage}
                enableCcRecipients
                requestType={REQUEST_TYPE.INTAKE}
              />
            </Grid>
          </Grid>
        </Content>
      </ContentWrapper>

      <AssignAdminModal
        open={isReassignOpen}
        onClose={closeReassign}
        onAssign={handleAssign}
      />
      <EditStatusModal
        open={isEditOpen}
        onClose={closeEdit}
        currentStatus={displayRequest.status}
        isLoading={isUpdatingStatus}
        onSave={handleSaveStatus}
      />
    </>
  );
};

export default IntakeRequestDetails;
